import { memo } from 'react';

import classNames from 'classnames';
import isEqual from 'fast-deep-equal';

import type { BaseHtmlAttributesProps } from '#types/base-html-attributes.type';

import { DEFAULT_PAGE_SIZE_OPTIONS } from '#pagination/constants/default-page-size-options';

import type { ContainerProps } from './types/container.props';

import { useContainerConfiguration } from './hooks/container-configuration.hook';
import { JumpToPage } from './jump-to-page';
import PageSize from './page-size';
import Pages from './pages';
import Total from './total';

import cssClasses from './container.module.css';

/**
 * Pagination container component that manages and displays pagination controls,
 * including page size selector, total count, and page navigation buttons.
 *
 * @param className - Additional CSS class names to apply to the container.
 * @param disabled - If true, disables all pagination controls.
 * @param isLoading - If true, displays loading state for pagination controls.
 * @param maxVisiblePages - Maximum number of visible page buttons (default: 5).
 * @param onPage - Callback fired when the page is changed.
 * @param onPageSize - Callback fired when the page size is changed.
 * @param page - Current page number (default: 1).
 * @param pageSize - Current page size (default: 10).
 * @param pageSizes - Array of available page size options.
 * @param showFirstLastButtons - If true, shows "First" and "Last" navigation buttons (default: true).
 * @param showPages - If true, displays the page navigation controls (default: true).
 * @param showPageSizes - If true, displays the page size selector (default: true).
 * @param total - Total number of items (default: 0).
 * @param totalType - Type of total to display, e.g., 'recordCount' (default: 'recordCount').
 * @param props - Additional HTML attributes to apply to the container.
 *
 * @returns The pagination container component.
 *
 * @remarks
 * This component is memoized using a deep equality check to prevent unnecessary re-renders.
 */
export const Container = ({
  className,
  disabled = false,
  isLoading = false,
  maxVisiblePages = 5,
  onPage,
  onPageSize,
  page,
  pageSize,
  pageSizes = DEFAULT_PAGE_SIZE_OPTIONS,
  position = 'bottom',
  showFirstLastButtons = true,
  showJumpToPage = true,
  showPages = true,
  showPageSizes = true,
  total,
  totalType = 'recordCount',
  ...props
}: BaseHtmlAttributesProps & ContainerProps) => {
  const {
    handleFirstPageClick,
    handleLastPageClick,
    handleNextPageClick,
    handlePageClick,
    handlePageSizeChanged,
    handlePreviousPageClick,
  } = useContainerConfiguration({ onPage, onPageSize, page, pageSize, total });

  return (
    <div
      className={classNames(cssClasses.paginationWrapper, className, {
        [cssClasses.bottom ?? '']: position === 'bottom',
        [cssClasses.top ?? '']: position === 'top',
        disabled,
      })}
      {...props}>
      {showPageSizes && (
        <PageSize
          disabled={disabled}
          onPageSizeChanged={handlePageSizeChanged}
          pageSize={pageSize}
          pageSizes={pageSizes}
        />
      )}
      <div className={cssClasses.sizeAndTotal}>
        <Total totalType={totalType} />
        {showJumpToPage && (
          <JumpToPage
            disabled={disabled}
            onPageChange={handlePageClick}
          />
        )}
      </div>
      {showPages && (
        <Pages
          isLoading={isLoading}
          maxVisiblePages={maxVisiblePages}
          onFirstPage={handleFirstPageClick}
          onLastPage={handleLastPageClick}
          onNextPage={handleNextPageClick}
          onPage={handlePageClick}
          onPreviousPage={handlePreviousPageClick}
          showFirstLastButtons={showFirstLastButtons}
        />
      )}
    </div>
  );
};

export default memo(Container, (prev, next) => {
  return isEqual(prev, next);
}) as typeof Container;
